/* @pareto-engineering/generator-front 1.0.12 */
import React, {
  useMemo,
} from 'react'

import {
  Link,
} from 'react-router-dom'

import styleNames from '@pareto-engineering/bem/exports'

import {
  AnimatedGradient,
  Page,
  useApp,
} from '@pareto-engineering/design-system'

import {
  SectionHero,
  Footer,
  NavigationBar,
  UseCaseCard,
} from 'modules/components'

import {
  SITE,
} from 'modules/urls'

const baseClassName = styleNames.base

const componentClassName = 'page-careers'

const coreValues = [
  {
    title      :'Collaboration',
    description:'We thrive on deep collaboration, and we prioritize listening and sharing across stakeholders. We cherish the contributions of our team members, AI Trainers, and partners.',
    image      :'/misc/careers-collaboration.webp',
  },
  {
    title      :'Results',
    description:'We deliver on our promises, believing in growth through hard work, effective strategies, and diverse feedback. We hire based on potential and progress, not just background.',
    image      :'/misc/careers-results.webp',
  },
  {
    title      :'Efficiency',
    description:'Our edge lies in rigorously sharpening our best practices, eliminating duplication, and empowering each team member to be self-managed in achieving their goals.',
    image      :'/misc/careers-efficiency.webp',
  },
  {
    title      :'Diversity & Inclusion',
    description:'We hire globally and actively cultivate a diverse and inclusive culture. We support all employees in achieving their personal and professional aspirations.',
    image      :'/misc/careers-diversity.webp',
  },
  {
    title      :'Iteration',
    description:'Constant learning keeps us resilient. We cherish curiosity, consistently refining solutions, learning new skills, and challenging existing knowledge.',
    image      :'/misc/careers-iteration.webp',
  },
  {
    title      :'Transparency',
    description:'Radical candor is our ethos. We address issues openly and celebrate the effort and vulnerability it takes to be transparent, start tough conversations, and introduce fresh ideas.',
    image      :'/misc/careers-transparency.webp',
  },
]

const Careers = () => {
  const app = useApp()

  const helmetProps = useMemo(() => ({
    title           :'Careers | Pareto.AI',
    meta_description:'Help us accelerate the development of cutting-edge AI models and connect talented professionals with AI training opportunities.',
    canonical       :SITE.CAREERS,
    og_type         :'website',
    main_image      :`${app.SITE.CANONICAL}/misc/social-share.jpg`,
  }), [])

  return (
    <Page
      id="page-careers"
      helmetProps={helmetProps}
      Background={AnimatedGradient}
      className={[
        baseClassName,
        componentClassName,
      ]
        .filter((e) => e)
        .join(' ')}
    >
      <NavigationBar
        overrideButtonToScrollToId="careers-apply"
      />

      <SectionHero
        className="to-plain"
        heading={(
          <>
            <span>
              Join us in shaping
            </span>
            {' '}
            <span className="x-highlighted c-x">
              the future of work
            </span>
            {' '}
            <span>
              with AI
            </span>
          </>
        )}
        subtitle="The nature of work is changing. We're on a mission to connect top talent with cutting-edge AI training jobs and help companies build AI models that are deeply aligned with human values."
      />

      <Page.Section
        id="careers-vision"
        className="x-background-far b-x"
      >
        <p className="h1 span-8 md-start-2 md-span-6">
          <span className="x-highlighted c-x">
            Our vision
          </span>
        </p>
        <div className="span-grid grid dense">
          <img
            alt="pareto nairobi meetup"
            src="/misc/careers-nairobi.webp"
            className="span-8 md-start-9 md-span-5"
            style={{
              borderRadius:'var(--theme-default-border-radius)',
              gridRow     :'span 4',
              width       :'100%',
              objectFit   :'cover',
            }}
          />
          <p className="s2 span-8 md-start-2 md-span-6 remove-top-margin">
            We believe that AI will create thousands of jobs for people around the world.
            Our team is committed to training and engaging diverse talent worldwide in the
            development of helpful, honest, and harmless Al models.
          </p>
          <p className="s2 span-8 md-start-2 md-span-6 remove-top-margin">
            Collaborating with experienced AI Trainers and crowd work researchers at Stanford
            and UPenn,
            {' '}
            <span className="x-highlighted c-x">
              we are building a human data collection platform centered on fair and
              ethical work practices.
            </span>
          </p>
          <p className="s2 span-8 md-start-2 md-span-6 remove-top-margin">
            We are designing research-backed incentive systems to empower workers and enhance
            job performance. This not only provides meaningful work for AI Trainers but also helps
            AI companies achieve higher quality data at lower costs.
          </p>
        </div>
      </Page.Section>

      <Page.Section
        id="careers-platform"
        className="x-background-far b-x"
      >
        <img
          alt="pareto team google meets meeting"
          src="/misc/careers-team.webp"
          className="span-8 md-start-2 md-span-5 h1"
          style={{
            borderRadius:'var(--theme-default-border-radius)',
            gridRow     :'span 5',
            width       :'100%',
            objectFit   :'cover',
            alignSelf   :'center',
          }}
        />
        <p className="h1 span-8 md-start-8 md-span-4">
          <span className="x-highlighted c-x">
            Our platform
          </span>
        </p>
        <p className="s2 span-8 md-start-8 md-span-6 remove-top-margin">
          At Pareto.AI, we match AI companies with extraordinary subject matter experts,
          helping AI developers fine-tune their models with the highest quality training
          data at unbeatable prices. Our team is at the forefront of AI development,
          building a talent platform to enable direct collaboration between AI researchers
          and experts across medicine, finance, engineering, law, and many more domains
          to train bespoke AI models.
        </p>
        <p className="s2 span-8 md-start-8 md-span-6 remove-top-margin">
          Pareto is part of StartX, a prestigious startup accelerator in Silicon Valley.
          We have a
          {' '}
          <span className="x-highlighted c-x">
            20-member core team along with a 150-member operations team.
          </span>
        </p>
      </Page.Section>

      <Page.Section
        id="careers-story"
        className="x-background-far b-x"
      >
        <p className="h1 span-8 md-start-2 md-span-6">
          <span className="x-highlighted c-x">
            Our Story
          </span>
        </p>
        <div className="span-grid grid dense">
          <img
            alt="pareto-core-team-google-meet-session"
            src="/misc/careers-ops.webp"
            className="span-8 md-start-9 md-span-5"
            style={{
              borderRadius:'var(--theme-default-border-radius)',
              gridRow     :'span 5',
              width       :'100%',
              objectFit   :'cover',
            }}
          />
          <p className="s2 span-8 md-start-2 md-span-6 remove-top-margin">
            Pareto.AI was founded by
            {' '}
            <span className="x-highlighted c-x">
              Phoebe Yao, Thiel Fellow and Forbes 30 under 30.
            </span>
            {' '}
            Phoebe was born in China before moving to St. Louis at 5 years of age.
            When she began studying at Stanford, she saw how technology could empower
            people worldwide with economic opportunities.
          </p>
          <p className="s2 span-8 md-start-2 md-span-6 remove-top-margin">
            Phoebe decided to start an online bootcamp to train women & work-at-home moms
            to be virtual assistants. While she was training over 20 women, the COVID-19
            pandemic hit. Witnessing the widespread unemployment, she dropped out of
            school to transform the bootcamp into a virtual assistant startup.
          </p>
          <p className="s2 span-8 md-start-2 md-span-6 remove-top-margin">
            After working on many data collection projects, hearing the frustrations of veteran
            crowd workers, and brainstorming with leading AI researchers, Phoebe decided to pivot
            Pareto.
          </p>
          <p className="s2 span-8 md-start-2 md-span-6 remove-top-margin">
            Fast forward to the present day, Pareto is harnessing top talent for cutting-edge AI
            development and creating a fair and ethical job platform poised to shape the future of
            work.
          </p>
        </div>
      </Page.Section>

      <Page.Section
        id="careers-use-cases"
        className="from-plain"
      >
        <p className="h1 span-8 md-start-2 md-span-6">
          <span className="x-highlighted c-x">
            Our Core Values
          </span>
        </p>
        <div className="span-grid grid">
          <UseCaseCard.Group
            className="span-8 md-start-2 md-span-12"
            desktopType="grid"
            type="snap-scroller"
          >
            {
              coreValues.map((useCase) => (
                <UseCaseCard
                  id={useCase.title}
                  {...useCase}
                  className="span-7 md-span-4 manual-mobile-only"
                />
              ))
            }
          </UseCaseCard.Group>
        </div>
      </Page.Section>

      <Page.Section
        id="careers-title"
        className="to-plain"
      >
        <div
          id="careers-apply"
          className="span-8 md-start-4 md-span-8 centered-column grid"
          style={{
            marginBottom:'var(--theme-default-section-spacing)',
          }}
        >
          <p className="h1">
            <span className="x-highlighted c-x">
              Apply to join our team
            </span>
          </p>
          <p>
            If you&apos;re interested in working as an AI Trainer, please apply to join our
            {' '}
            <Link
              to={SITE.AI_TRAINER}
            >
              <span
                className="x-interactive c-x no-wrap"
              >
                AI projects community instead.
              </span>
            </Link>
          </p>
        </div>
      </Page.Section>

      <Page.Section
        id="careers-board"
        className="x-background-far b-x"
      >
        <style>
          {`
            #ashby_embed > iframe {
              border-radius: var(--theme-default-border-radius);
            }
          `}
        </style>
        <div
          id="ashby_embed"
          className="span-8 md-start-2 md-span-12"
        />
      </Page.Section>

      <Page.Section
        id="careers-footer"
        className="from-plain"
        isSpaced
      />
      <Footer />
    </Page>
  )
}

export default Careers
